import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Ban, Eye, FileText, Plus, Trash2, TriangleAlert } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import {
  DataCard,
  DataCardActions,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardStatus,
  DataCardTitle,
} from "@/components/ui/data-card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import { consumer } from "@/lib/actioncable"
import {
  type BriefStatus,
  StatusBadge,
  isActiveStatus,
} from "./brief-shared"

type BriefRow = {
  id: number
  period_label: string
  period_grain: string
  status: BriefStatus
  trigger: string
  signals_count: number
  foresights_count: number
  action_items_count: number
  needs_review: boolean
  cost: number
  error_message: string | null
  generated_at: string | null
  published_at: string | null
  created_at: string
  created_by: string | null
}

type Filters = { status: string | null; period: string | null }
type PeriodOption = { value: string; label: string }

type StatusUpdate = {
  type: "status_update"
  brief_id: number
  status: BriefStatus
  signals_count?: number
  error_message?: string | null
  generated_at?: string | null
}

export default function AdminBriefsIndex({
  briefs,
  in_flight,
  total,
  page,
  per_page,
  filters,
  filter_options,
  assembler_enabled,
}: {
  briefs: BriefRow[]
  in_flight: BriefRow[]
  total: number
  page: number
  per_page: number
  filters: Filters
  filter_options: { statuses: BriefStatus[]; periods: PeriodOption[] }
  assembler_enabled: boolean
}) {
  const [liveBriefs, setLiveBriefs] = React.useState<BriefRow[]>(briefs)
  const [liveInFlight, setLiveInFlight] = React.useState<BriefRow[]>(in_flight)

  React.useEffect(() => setLiveBriefs(briefs), [briefs])
  React.useEffect(() => setLiveInFlight(in_flight), [in_flight])

  // Subscribe to the full active set, sourced off the in-flight panel.
  // SSR-safe: ActionCable is only touched inside this effect.
  const liveKey = liveInFlight.map((b) => b.id).sort((a, b) => a - b).join(",")

  React.useEffect(() => {
    if (!liveKey) return
    const ids = liveKey.split(",").map((s) => parseInt(s, 10))

    function merge(prev: BriefRow[], data: StatusUpdate): BriefRow[] {
      return prev.map((b) =>
        b.id === data.brief_id
          ? {
              ...b,
              status: data.status,
              signals_count: data.signals_count ?? b.signals_count,
              error_message: data.error_message ?? b.error_message,
              generated_at: data.generated_at ?? b.generated_at,
            }
          : b,
      )
    }

    const subs = ids.map((id) =>
      consumer.subscriptions.create(
        { channel: "BriefRunChannel", brief_id: id },
        {
          received(data: StatusUpdate) {
            setLiveBriefs((prev) => merge(prev, data))
            if (isActiveStatus(data.status, assembler_enabled)) {
              setLiveInFlight((prev) => merge(prev, data))
            } else {
              setLiveInFlight((prev) => prev.filter((b) => b.id !== data.brief_id))
              router.reload({ only: ["briefs", "in_flight", "filter_options"] })
            }
          },
        },
      ),
    )
    return () => subs.forEach((s) => s.unsubscribe())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveKey, assembler_enabled])

  function navigate(overrides: Record<string, string | number | null>) {
    const params: Record<string, string | number> = {}
    if (filters.status) params.status = filters.status
    if (filters.period) params.period = filters.period
    if (page > 1) params.page = page
    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") params[k] = v as string | number
      else delete params[k]
    })
    router.get("/admin/briefs", params as Record<string, string>, { preserveScroll: false })
  }

  function handleCancel(id: number) {
    router.patch(`/admin/briefs/${id}/cancel`)
  }

  function handleDelete(brief: BriefRow) {
    if (!window.confirm(`Hapus brief ${brief.period_label}?`)) return
    router.delete(`/admin/briefs/${brief.id}`)
  }

  const hasActiveFilter = !!filters.status || !!filters.period
  const totalPages = Math.ceil(total / per_page)

  return (
    <>
      <Head title="Brief Mingguan">
        <meta
          name="description"
          content="Konsol Brief Mingguan — sintesis 3 bagian dari metrik yang sudah direkonsiliasi, setiap angka tersitasi, bisa diedit dan diekspor."
        />
        <meta property="og:title" content="Brief Mingguan" />
        <meta
          property="og:description"
          content="Konsol Brief Mingguan — sintesis 3 bagian dari metrik yang sudah direkonsiliasi, setiap angka tersitasi, bisa diedit dan diekspor."
        />
      </Head>
      <AppShell width="wide">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-hairline pb-6">
          <div className="flex items-start gap-3">
            <FileText className="mt-1 h-6 w-6 shrink-0 text-ink-muted" />
            <div>
              <h1>Brief Mingguan</h1>
              <p className="mt-1">
                Sintesis mingguan otomatis atas metrik yang sudah direkonsiliasi (pangsa,
                service level, stok, kompetitor). Claude hanya menarasikan; setiap angka harus
                tersitasi ke sumbernya.
              </p>
            </div>
          </div>
          <Button asChild variant="primary" className="shrink-0 gap-2">
            <Link href="/admin/briefs/new">
              <Plus className="h-4 w-4" />
              Buat Brief
            </Link>
          </Button>
        </div>

        {!assembler_enabled && (
          <p className="mt-4 rounded-md border border-hairline bg-surface px-4 py-2 text-xs text-ink-muted">
            Narasi AI (kill-switch <code>WEEKLY_BRIEF</code>) nonaktif — brief berhenti di lapisan
            sinyal deterministik. Sinyal &amp; konsol tetap berjalan tanpa LLM.
          </p>
        )}

        {/* In-flight panel */}
        {liveInFlight.length > 0 && (
          <div className="mt-8">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="mb-0">Sedang Diproses</h2>
              <Badge tone="accent">{liveInFlight.length} aktif</Badge>
            </div>
            <div className="space-y-2">
              {liveInFlight.map((b) => (
                <div key={b.id} className="rounded-md border border-hairline bg-surface px-4 py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <StatusBadge status={b.status} />
                    <Link href={`/admin/briefs/${b.id}`} className="font-medium text-ink-display hover:text-accent">
                      {b.period_label}
                    </Link>
                    {b.created_by && <span className="text-xs text-ink-muted">oleh {b.created_by}</span>}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto gap-1 text-red-600"
                      onClick={() => handleCancel(b.id)}
                    >
                      <Ban className="h-3.5 w-3.5" />
                      Batalkan
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        <div className="mt-10">
          <h2 className="mb-4">Riwayat Brief</h2>

          <div className="mb-4 flex flex-wrap items-end gap-3">
            <Select
              value={filters.status ?? ""}
              onChange={(e) => navigate({ status: e.target.value || null, page: null })}
              className="w-44"
            >
              <option value="">Semua Status</option>
              {filter_options.statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
            <Select
              value={filters.period ?? ""}
              onChange={(e) => navigate({ period: e.target.value || null, page: null })}
              className="w-40"
            >
              <option value="">Semua Periode</option>
              {filter_options.periods.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>
            {hasActiveFilter && (
              <Button variant="ghost" size="sm" onClick={() => navigate({ status: null, period: null, page: null })}>
                Reset filter
              </Button>
            )}
          </div>

          {liveBriefs.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {hasActiveFilter ? "Tidak ada brief yang cocok dengan filter." : "Belum ada brief."}
            </p>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="space-y-3 md:hidden">
                {liveBriefs.map((b) => (
                  <BriefCard key={b.id} brief={b} onCancel={() => handleCancel(b.id)} onDelete={() => handleDelete(b)} assemblerEnabled={assembler_enabled} />
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-hidden rounded-md border border-hairline md:block">
                <table className="w-full text-sm">
                  <thead className="bg-surface">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Periode</th>
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Status</th>
                      <th className="px-4 py-2.5 text-right font-medium text-ink-muted">Sinyal</th>
                      <th className="px-4 py-2.5 text-right font-medium text-ink-muted">Foresight</th>
                      <th className="px-4 py-2.5 text-right font-medium text-ink-muted">Aksi</th>
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Oleh</th>
                      <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Dibuat</th>
                      <th className="w-10 px-2 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {liveBriefs.map((b) => (
                      <BriefTableRow
                        key={b.id}
                        brief={b}
                        onCancel={() => handleCancel(b.id)}
                        onDelete={() => handleDelete(b)}
                        assemblerEnabled={assembler_enabled}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {total > 0 && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-muted">
                Menampilkan {(page - 1) * per_page + 1}–{Math.min(page * per_page, total)} dari {total} brief
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => navigate({ page: page - 1 })}>
                    Sebelumnya
                  </Button>
                  <span className="text-sm text-ink-muted">Hal. {page} / {totalPages}</span>
                  <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => navigate({ page: page + 1 })}>
                    Berikutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </AppShell>
    </>
  )
}

function BriefTableRow({
  brief,
  onCancel,
  onDelete,
  assemblerEnabled,
}: {
  brief: BriefRow
  onCancel: () => void
  onDelete: () => void
  assemblerEnabled: boolean
}) {
  const active = isActiveStatus(brief.status, assemblerEnabled)
  return (
    <tr>
      <td className="px-4 py-3 font-medium text-ink-display">
        <Link href={`/admin/briefs/${brief.id}`} className="hover:text-accent hover:underline">
          {brief.period_label}
        </Link>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <StatusBadge status={brief.status} />
          {brief.needs_review && <TriangleAlert className="h-3.5 w-3.5 text-amber-500" aria-label="Perlu ditinjau" />}
        </div>
        {brief.error_message && (
          <p className="mt-1 max-w-[240px] truncate text-xs text-red-600 dark:text-red-400" title={brief.error_message}>
            {brief.error_message}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">{brief.signals_count}</td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">{brief.foresights_count}</td>
      <td className="px-4 py-3 text-right tabular-nums text-ink-body">{brief.action_items_count}</td>
      <td className="px-4 py-3 text-xs text-ink-muted">{brief.created_by ?? "—"}</td>
      <td className="px-4 py-3 text-xs text-ink-muted">{formatDate(brief.created_at)}</td>
      <td className="px-2 py-3">
        <div className="flex items-center justify-end gap-1">
          <Link href={`/admin/briefs/${brief.id}`} title="Lihat brief" className="rounded p-1 text-ink-muted transition-colors hover:text-accent">
            <Eye className="h-3.5 w-3.5" />
          </Link>
          {active ? (
            <button onClick={onCancel} title="Batalkan" className="rounded p-1 text-ink-muted transition-colors hover:text-red-600">
              <Ban className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button onClick={onDelete} title="Hapus" className="rounded p-1 text-ink-muted transition-colors hover:text-red-600">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

function BriefCard({
  brief,
  onCancel,
  onDelete,
  assemblerEnabled,
}: {
  brief: BriefRow
  onCancel: () => void
  onDelete: () => void
  assemblerEnabled: boolean
}) {
  const active = isActiveStatus(brief.status, assemblerEnabled)
  return (
    <DataCard>
      <DataCardHeader>
        <DataCardTitle>{brief.period_label}</DataCardTitle>
        <DataCardStatus><StatusBadge status={brief.status} /></DataCardStatus>
      </DataCardHeader>
      <DataCardGrid>
        <DataCardField label="Sinyal" value={String(brief.signals_count)} />
        <DataCardField label="Foresight" value={String(brief.foresights_count)} />
        <DataCardField label="Action item" value={String(brief.action_items_count)} />
        <DataCardField label="Oleh" value={brief.created_by ?? "—"} />
        <DataCardField wide label="Dibuat" value={formatDate(brief.created_at)} />
        {brief.error_message && <DataCardField wide label="Error" value={brief.error_message} />}
      </DataCardGrid>
      <DataCardActions>
        <Button asChild variant="ghost" size="sm" className="gap-2">
          <Link href={`/admin/briefs/${brief.id}`}>
            <Eye className="h-4 w-4" />
            Lihat
          </Link>
        </Button>
        {active ? (
          <Button variant="ghost" size="sm" onClick={onCancel} className="gap-2 text-red-600">
            <Ban className="h-4 w-4" />
            Batalkan
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={onDelete} className="gap-2 text-red-600">
            <Trash2 className="h-4 w-4" />
            Hapus
          </Button>
        )}
      </DataCardActions>
    </DataCard>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
